/**
 * Document Center Section
 * Handles category navigation, document display, and search functionality
 * Supports both desktop (sidebar) and mobile (tabs + dropdown) layouts
 */

class DocumentCenter extends HTMLElement {
  constructor() {
    super();

    this.data = null;
    this.currentCategory = null;
    this.currentItem = null;
    this.searchQuery = "";
    this.isMobile = false;

    // DOM references - Desktop
    this.categoryList = this.querySelector(".document-center__category-list");

    // DOM references - Mobile
    this.tabsContainer = this.querySelector(".document-center__tabs");
    this.filterDropdown = this.querySelector(".document-center__filter");
    this.filterTrigger = this.querySelector(".document-center__filter-trigger");
    this.filterText = this.querySelector(".document-center__filter-text");
    this.filterMenu = this.querySelector(".document-center__filter-menu");

    // DOM references - Shared
    this.documentsContainer = this.querySelector(".document-center__documents");
    this.emptyState = this.querySelector(".document-center__empty");
    this.searchInput = this.querySelector(".document-center__search-input");
    this.searchBtn = this.querySelector(".document-center__search-btn");
  }

  connectedCallback() {
    this.checkViewport();
    this.loadData();
    this.bindEvents();

    // Listen for viewport changes
    window.addEventListener(
      "resize",
      this.debounce(() => {
        const wasMobile = this.isMobile;
        this.checkViewport();
        if (wasMobile !== this.isMobile) {
          this.handleViewportChange();
        }
      }, 150)
    );
  }

  /**
   * Check if viewport is mobile
   */
  checkViewport() {
    this.isMobile = window.innerWidth < 990;
  }

  /**
   * Handle viewport change between mobile and desktop
   */
  handleViewportChange() {
    // Re-render based on current state
    if (this.isMobile) {
      this.updateMobileFilterOptions();
      this.updateActiveTab();
    }
    this.renderDocuments();
  }

  /**
   * Load JSON data from embedded script tag
   */
  loadData() {
    const dataScript = this.querySelector("[data-document-center-data]");
    if (!dataScript) {
      console.error("Document center data not found");
      return;
    }

    try {
      this.data = JSON.parse(dataScript.textContent);
      this.renderCategories();
      this.renderMobileTabs();

      // Auto-select first category and first item with documents
      if (this.data.categories?.length > 0) {
        this.selectFirstAvailable();
      }
    } catch (e) {
      console.error("Failed to parse document center data:", e);
    }
  }

  /**
   * Bind event listeners
   */
  bindEvents() {
    // Search input
    this.searchInput?.addEventListener(
      "input",
      this.debounce((e) => {
        this.searchQuery = e.target.value.trim().toLowerCase();
        this.renderDocuments();
      }, 300)
    );

    // Search button
    this.searchBtn?.addEventListener("click", () => {
      this.searchQuery = this.searchInput?.value.trim().toLowerCase() || "";
      this.renderDocuments();
    });

    // Enter key in search
    this.searchInput?.addEventListener("keydown", (e) => {
      if (e.key === "Enter") {
        this.searchQuery = e.target.value.trim().toLowerCase();
        this.renderDocuments();
      }
    });

    // Mobile filter dropdown - trigger toggle
    this.filterTrigger?.addEventListener("click", (e) => {
      e.stopPropagation();
      this.toggleFilterDropdown();
    });

    // Mobile filter dropdown - item selection (event delegation)
    this.filterMenu?.addEventListener("click", (e) => {
      const item = e.target.closest(".document-center__filter-item");
      if (!item) return;

      const itemId = item.dataset.value;
      if (itemId === "all") {
        this.currentItem = null;
      } else {
        this.currentItem = itemId;
      }

      this.updateFilterDropdownState();
      this.closeFilterDropdown();
      this.renderDocuments();
    });

    // Close dropdown when clicking outside
    document.addEventListener("click", (e) => {
      if (this.filterDropdown && !this.filterDropdown.contains(e.target)) {
        this.closeFilterDropdown();
      }
    });

    // Download button click (event delegation)
    this.documentsContainer?.addEventListener("click", (e) => {
      const downloadBtn = e.target.closest("[data-download-url]");
      if (downloadBtn) {
        e.preventDefault();
        const url = downloadBtn.dataset.downloadUrl;
        const filename = downloadBtn.dataset.filename;
        this.handleDownload(url, filename);
      }
    });
  }

  /**
   * Handle file download by fetching and creating blob
   * This bypasses cross-origin restrictions on the download attribute
   */
  async handleDownload(url, filename) {
    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error("Download failed");
      }
      
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = filename || "document";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up blob URL
      URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Download error:", error);
      // Fallback: open in new tab
      window.open(url, "_blank");
    }
  }

  /**
   * Toggle filter dropdown open/closed
   */
  toggleFilterDropdown() {
    if (!this.filterDropdown || !this.filterTrigger) return;

    const isOpen = this.filterDropdown.dataset.open === "true";
    this.filterDropdown.dataset.open = String(!isOpen);
    this.filterTrigger.setAttribute("aria-expanded", String(!isOpen));
  }

  /**
   * Close filter dropdown
   */
  closeFilterDropdown() {
    if (!this.filterDropdown || !this.filterTrigger) return;

    this.filterDropdown.dataset.open = "false";
    this.filterTrigger.setAttribute("aria-expanded", "false");
  }

  /**
   * Update filter dropdown active state
   */
  updateFilterDropdownState() {
    if (!this.filterMenu || !this.filterText) return;

    const items = this.filterMenu.querySelectorAll(
      ".document-center__filter-item"
    );
    items.forEach((item) => {
      const itemValue = item.dataset.value;
      const isActive =
        (itemValue === "all" && this.currentItem === null) ||
        itemValue === this.currentItem;

      item.classList.toggle("document-center__filter-item--active", isActive);
      item.setAttribute("aria-selected", String(isActive));

      if (isActive) {
        this.filterText.textContent = item.textContent?.trim() || "";
      }
    });
  }

  /**
   * Select "All" category by default to show all documents
   */
  selectFirstAvailable() {
    // Default to "All" category - show all documents
    this.currentCategory = null;
    this.currentItem = null;

    // Desktop: ensure "All" button is active
    this.updateAllCategoryState();

    // Mobile: update tabs and dropdown
    this.updateActiveTab();
    this.updateMobileFilterOptions();

    this.renderDocuments();
  }

  /**
   * Render desktop category navigation
   */
  renderCategories() {
    if (!this.categoryList || !this.data?.categories) return;

    // Create "All" category item first
    const allCategoryHtml = `
      <li class="document-center__category document-center__category--all" data-category-id="all">
        <button 
          type="button" 
          class="document-center__category-header document-center__category-header--all is-active"
          data-category-id="all"
        >
          <span>All</span>
        </button>
      </li>
    `;

    // Create regular category items
    const categoriesHtml = this.data.categories
      .map(
        (category) => `
      <li class="document-center__category" data-category-id="${category.id}">
        <button 
          type="button" 
          class="document-center__category-header"
          aria-expanded="false"
          aria-controls="items-${category.id}"
        >
          <span>${this.escapeHtml(category.name)}</span>
          <svg class="document-center__category-icon" width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M2.5 4.5L6 8L9.5 4.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>
        <ul 
          id="items-${category.id}" 
          class="document-center__item-list" 
          role="list"
        >
          ${
            category.items
              ?.map(
                (item) => `
            <li class="document-center__item">
              <button 
                type="button" 
                class="document-center__item-btn"
                data-category-id="${category.id}"
                data-item-id="${item.id}"
              >
                ${this.escapeHtml(item.name)}
              </button>
            </li>
          `
              )
              .join("") || ""
          }
        </ul>
      </li>
    `
      )
      .join("");

    this.categoryList.innerHTML = allCategoryHtml + categoriesHtml;

    // Bind "All" category click event
    const allCategoryBtn = this.categoryList.querySelector('.document-center__category-header--all');
    allCategoryBtn?.addEventListener("click", () => {
      this.selectAllCategory();
    });

    // Bind category toggle events
    this.categoryList
      .querySelectorAll(".document-center__category-header:not(.document-center__category-header--all)")
      .forEach((header) => {
        header.addEventListener("click", (e) => {
          const categoryEl = e.target.closest(".document-center__category");
          const categoryId = categoryEl?.dataset.categoryId;
          if (categoryId) {
            this.toggleCategory(categoryId);
          }
        });
      });

    // Bind item click events
    this.categoryList
      .querySelectorAll(".document-center__item-btn")
      .forEach((btn) => {
        btn.addEventListener("click", (e) => {
          const categoryId = e.target.dataset.categoryId;
          const itemId = e.target.dataset.itemId;
          this.selectItem(categoryId, itemId);
        });
      });
  }

  /**
   * Select "All" category to show all documents (desktop)
   */
  selectAllCategory() {
    this.currentCategory = null;
    this.currentItem = null;
    this.updateAllCategoryState();
    this.renderDocuments();
  }

  /**
   * Update "All" category active state (desktop)
   */
  updateAllCategoryState() {
    if (!this.categoryList) return;

    const allBtn = this.categoryList.querySelector('.document-center__category-header--all');
    
    // Remove active from all item buttons
    this.categoryList
      .querySelectorAll(".document-center__item-btn")
      .forEach((btn) => {
        btn.classList.remove("is-active");
      });

    // Set "All" button as active
    allBtn?.classList.add("is-active");
  }

  /**
   * Render mobile tabs
   */
  renderMobileTabs() {
    if (!this.tabsContainer || !this.data?.categories) return;

    // Add "All" tab plus category tabs
    this.tabsContainer.innerHTML = `
      <button type="button" class="document-center__tab is-active" data-category-id="all" role="tab">
        All
      </button>
      ${this.data.categories
        .map(
          (category) => `
        <button 
          type="button" 
          class="document-center__tab" 
          data-category-id="${category.id}"
          role="tab"
        >
          ${this.escapeHtml(category.name)}
        </button>
      `
        )
        .join("")}
    `;

    // Bind tab click events
    this.tabsContainer
      .querySelectorAll(".document-center__tab")
      .forEach((tab) => {
        tab.addEventListener("click", (e) => {
          const categoryId = e.target.dataset.categoryId;
          this.selectMobileCategory(categoryId);
        });
      });
  }

  /**
   * Select category from mobile tabs
   */
  selectMobileCategory(categoryId) {
    if (categoryId === "all") {
      this.currentCategory = null;
      this.currentItem = null;
    } else {
      this.currentCategory = categoryId;
      this.currentItem = null; // Reset item selection
    }

    this.updateActiveTab();
    this.updateMobileFilterOptions();
    this.renderDocuments();
  }

  /**
   * Update active tab styling
   */
  updateActiveTab() {
    if (!this.tabsContainer) return;

    this.tabsContainer
      .querySelectorAll(".document-center__tab")
      .forEach((tab) => {
        tab.classList.remove("is-active");
        const tabCategoryId = tab.dataset.categoryId;

        if (this.currentCategory === null && tabCategoryId === "all") {
          tab.classList.add("is-active");
        } else if (tabCategoryId === this.currentCategory) {
          tab.classList.add("is-active");
        }
      });
  }

  /**
   * Update mobile filter dropdown options based on selected category
   */
  updateMobileFilterOptions() {
    if (!this.filterMenu || !this.data?.categories) return;

    let items = [];

    if (this.currentCategory === null) {
      // "All" selected - show all items from all categories
      for (const category of this.data.categories) {
        for (const item of category.items || []) {
          items.push(item);
        }
      }
    } else {
      // Specific category selected
      const category = this.data.categories.find(
        (c) => c.id === this.currentCategory
      );
      items = category?.items || [];
    }

    const isAllActive = this.currentItem === null;

    this.filterMenu.innerHTML = `
      <li
        class="document-center__filter-item ${
          isAllActive ? "document-center__filter-item--active" : ""
        }"
        role="option"
        aria-selected="${isAllActive}"
        data-value="all"
      >
        All Models
      </li>
      ${items
        .map(
          (item) => `
        <li
          class="document-center__filter-item ${
            item.id === this.currentItem
              ? "document-center__filter-item--active"
              : ""
          }"
          role="option"
          aria-selected="${item.id === this.currentItem}"
          data-value="${item.id}"
        >
          ${this.escapeHtml(item.name)}
        </li>
      `
        )
        .join("")}
    `;

    // Update display text
    this.updateFilterDropdownState();
  }

  /**
   * Toggle category expansion (desktop)
   */
  toggleCategory(categoryId) {
    const categoryEl = this.categoryList?.querySelector(
      `[data-category-id="${categoryId}"]`
    );
    if (!categoryEl) return;

    const isExpanded = categoryEl.classList.contains("is-expanded");
    const header = categoryEl.querySelector(
      ".document-center__category-header"
    );

    if (isExpanded) {
      categoryEl.classList.remove("is-expanded");
      header?.setAttribute("aria-expanded", "false");
    } else {
      categoryEl.classList.add("is-expanded");
      header?.setAttribute("aria-expanded", "true");
    }
  }

  /**
   * Expand a specific category (desktop)
   */
  expandCategory(categoryId) {
    const categoryEl = this.categoryList?.querySelector(
      `[data-category-id="${categoryId}"]`
    );
    if (!categoryEl) return;

    const header = categoryEl.querySelector(
      ".document-center__category-header"
    );
    categoryEl.classList.add("is-expanded");
    header?.setAttribute("aria-expanded", "true");
  }

  /**
   * Select an item and show its documents (desktop)
   */
  selectItem(categoryId, itemId) {
    this.currentCategory = categoryId;
    this.currentItem = itemId;
    this.updateActiveItem(itemId);
    
    // Remove active state from "All" button
    const allBtn = this.categoryList?.querySelector('.document-center__category-header--all');
    allBtn?.classList.remove("is-active");
    
    this.renderDocuments();
  }

  /**
   * Update active item styling (desktop)
   */
  updateActiveItem(itemId) {
    if (!this.categoryList) return;

    // Remove active class from all items
    this.categoryList
      .querySelectorAll(".document-center__item-btn")
      .forEach((btn) => {
        btn.classList.remove("is-active");
      });

    // Add active class to selected item
    const activeBtn = this.categoryList.querySelector(
      `[data-item-id="${itemId}"]`
    );
    activeBtn?.classList.add("is-active");
  }

  /**
   * Render documents based on current selection and search
   */
  renderDocuments() {
    if (!this.documentsContainer) return;

    let documents = this.getFilteredDocuments();

    if (documents.length === 0) {
      this.documentsContainer.innerHTML = "";
      this.emptyState?.removeAttribute("hidden");
      return;
    }

    this.emptyState?.setAttribute("hidden", "");

    this.documentsContainer.innerHTML = documents
      .map(
        (doc) => `
      <article class="document-center__document" data-document-id="${doc.id}">
        <div class="document-center__document-info">
          <h3 class="document-center__document-title">${this.escapeHtml(
            doc.title
          )}</h3>
          <div class="document-center__document-meta">
            <span>Format: ${this.escapeHtml(doc.format)}</span>
            <span>Time: ${this.formatDate(doc.date)}</span>
          </div>
        </div>
        <div class="document-center__document-actions">
          <a 
            href="${this.escapeHtml(doc.preview_url)}" 
            class="document-center__action-link document-center__action-link--preview"
            target="_blank"
            rel="noopener"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M1.33334 8C1.33334 8 3.33334 2.66667 8 2.66667C12.6667 2.66667 14.6667 8 14.6667 8C14.6667 8 12.6667 13.3333 8 13.3333C3.33334 13.3333 1.33334 8 1.33334 8Z" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M8 10C9.10457 10 10 9.10457 10 8C10 6.89543 9.10457 6 8 6C6.89543 6 6 6.89543 6 8C6 9.10457 6.89543 10 8 10Z" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            Preview
          </a>
          <button 
            type="button"
            class="document-center__action-link document-center__action-link--download"
            data-download-url="${this.escapeHtml(doc.download_url)}"
            data-filename="${this.escapeHtml(doc.title)}.${this.escapeHtml(doc.format?.toLowerCase() || 'pdf')}"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M14 10V12.6667C14 13.0203 13.8595 13.3594 13.6095 13.6095C13.3594 13.8595 13.0203 14 12.6667 14H3.33333C2.97971 14 2.64057 13.8595 2.39052 13.6095C2.14048 13.3594 2 13.0203 2 12.6667V10" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M4.66666 6.66667L8 10L11.3333 6.66667" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M8 10V2" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            Download
          </button>
        </div>
      </article>
    `
      )
      .join("");
  }

  /**
   * Get filtered documents based on selection and search
   */
  getFilteredDocuments() {
    if (!this.data?.categories) return [];

    let documents = [];

    // If search query exists, search across all documents
    if (this.searchQuery) {
      for (const category of this.data.categories) {
        // If category is selected, filter by it
        if (this.currentCategory && category.id !== this.currentCategory) {
          continue;
        }

        for (const item of category.items || []) {
          // If item is selected, filter by it
          if (this.currentItem && item.id !== this.currentItem) {
            continue;
          }

          for (const doc of item.documents || []) {
            const matchesTitle = doc.title
              .toLowerCase()
              .includes(this.searchQuery);
            const matchesCategory = category.name
              .toLowerCase()
              .includes(this.searchQuery);
            const matchesItem = item.name
              .toLowerCase()
              .includes(this.searchQuery);

            if (matchesTitle || matchesCategory || matchesItem) {
              documents.push(doc);
            }
          }
        }
      }
      return documents;
    }

    // No search query - filter by selection
    if (this.currentCategory === null && this.currentItem === null) {
      // "All" selected - show all documents from all categories
      for (const category of this.data.categories) {
        for (const item of category.items || []) {
          documents = documents.concat(item.documents || []);
        }
      }
    } else if (this.isMobile) {
      // Mobile view logic
      if (this.currentCategory && this.currentItem === null) {
        // Specific category, all models
        const category = this.data.categories.find(
          (c) => c.id === this.currentCategory
        );
        for (const item of category?.items || []) {
          documents = documents.concat(item.documents || []);
        }
      } else if (this.currentItem) {
        // Specific item selected
        for (const category of this.data.categories) {
          const item = category.items?.find((i) => i.id === this.currentItem);
          if (item) {
            documents = item.documents || [];
            break;
          }
        }
      }
    } else {
      // Desktop view - show documents from selected item
      if (this.currentCategory && this.currentItem) {
        const category = this.data.categories.find(
          (c) => c.id === this.currentCategory
        );
        const item = category?.items?.find((i) => i.id === this.currentItem);
        documents = item?.documents || [];
      }
    }

    return documents;
  }

  /**
   * Format date string to DD/MM/YY
   */
  formatDate(dateStr) {
    if (!dateStr) return "";

    try {
      const date = new Date(dateStr);
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = String(date.getFullYear()).slice(-2);
      return `${day}/${month}/${year}`;
    } catch {
      return dateStr;
    }
  }

  /**
   * Escape HTML to prevent XSS
   */
  escapeHtml(str) {
    if (!str) return "";
    const div = document.createElement("div");
    div.textContent = str;
    return div.innerHTML;
  }

  /**
   * Debounce function for search input
   */
  debounce(fn, delay) {
    let timeoutId;
    return (...args) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => fn.apply(this, args), delay);
    };
  }
}

// Register custom element
if (!customElements.get("document-center")) {
  customElements.define("document-center", DocumentCenter);
}
